import React, { useState, useContext } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

interface InvoiceValidationPanelProps {
  invoiceId: number;
  onValidationComplete: () => void;
}

export const InvoiceValidationPanel: React.FC<InvoiceValidationPanelProps> = ({ invoiceId, onValidationComplete }) => {
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);
  const [validationStatus, setValidationStatus] = useState<string>('unvalidated');
  const [validationNotes, setValidationNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleValidation = async (status: 'approved' | 'rejected') => {
    setLoading(true);
    try {
      await axios.put(`/vendors/vendor_invoices/${invoiceId}/validate`, {
        validation_status: status,
        validation_notes: validationNotes,
      });
      showSuccessSnackBar(`Invoice ${status} successfully`);
      setValidationStatus(status);
      onValidationComplete();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || `Failed to ${status} invoice`;
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box style={{ marginTop: 20 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Invoice Validation
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Validation Status</InputLabel>
                <Select
                  value={validationStatus}
                  onChange={(e) => setValidationStatus(e.target.value)}
                  label="Validation Status"
                >
                  <MenuItem value="unvalidated">Unvalidated</MenuItem>
                  <MenuItem value="matched">Matched</MenuItem>
                  <MenuItem value="discrepancy">Discrepancy</MenuItem>
                  <MenuItem value="unmatched">Unmatched</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Validation Notes"
                value={validationNotes}
                onChange={(e) => setValidationNotes(e.target.value)}
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                placeholder="Add notes about the validation..."
              />
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleValidation('approved')}
                    disabled={loading}
                  >
                    Approve Invoice
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => handleValidation('rejected')}
                    disabled={loading}
                  >
                    Reject Invoice
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};
